import { inject, Injectable } from '@angular/core';

import {
  IdService,
  IfFeatureService,
  AccountsDescriptionService,
  NotificationsToastService,
  SymbolType,
  WebsocketMessage
} from '@tdwb/core';
import {
  EquityExtendedTerms,
  EquityOrderAction,
  EquityOrderItem,
  EquityOrderTerms,
  MultiLegEntry,
  MultiLegOrderTerms,
  OptionOrderAction,
  OptionOrderItem,
  OptionOrderTerms,
  OrderAccountInfo,
  OrderItem,
  OrderTerms,
  PricePlan,
  Strategy
} from '@tdwb/lazy/trading';
import { OrderNotificationsToast, OrderNotificationsToastAction, OrderNotificationsToastItem } from './order-notifications-toast.domain';
import { OrderNotificationsToastComponent } from './toast/order-notifications-toast.component';

/**
 * OrderNotificationsToastService subscribe to notifications service
 * Filter out order niotifications
 *
 * For each notification process data and set toast to be used for displaying notification
 * By calling degate function on NotificationsToastService
 *
 */

@Injectable({
  providedIn: 'root'
})
export class OrderNotificationsToastService {
  private static readonly ID_PREFIX: string = 'td-wb-order-notifications-toast';
  private static readonly REJECTED: string = 'REJECTED';
  private readonly accountsDescriptionService = inject(AccountsDescriptionService);
  private readonly notificationsService = inject(NotificationsToastService);
  private readonly featureService = inject(IfFeatureService);
  private readonly idService = inject(IdService);
  private readonly newMLONotificationEnabled: boolean;

  constructor() {
    this.newMLONotificationEnabled = this.featureService.getFeatureFlag<boolean>('newMLONotification');
  }

  /**
   * Init order notification service,
   * called from WebbrokerInitService to start listening to order notifications
   * Process data and set toast to be displayed
   */
  init(): void {
    this.notificationsService.init();
    this.notificationsService.onNotificationsReceived
      .subscribe((notifications: WebsocketMessage<OrderNotificationsToast>): void => {
        const toastOrders: Array<OrderNotificationsToast> =
          this.newMLONotificationEnabled ? this.processData(notifications) : this.processDataOld(notifications);
        toastOrders.forEach((toastOrder: OrderNotificationsToast): void => {
          this.notificationsService.setToast(toastOrder);
        });
      });
  }

  /**
   * Get toast data
   * Create componentFactory to be used by notofication toast
   */
  private getToast(notifications: WebsocketMessage<OrderNotificationsToast>): OrderNotificationsToast {
    return {
      type: notifications.type,
      componentType: OrderNotificationsToastComponent
    };
  }

  /**
   * Process order notification data into simple or complex order
   */
  private processData(notifications: WebsocketMessage<OrderNotificationsToast>): Array<OrderNotificationsToast> {
    const orderTerms = notifications.payload as OrderTerms;
    const toast: OrderNotificationsToast = this.getToast(notifications);
    let toasts: Array<OrderNotificationsToast> = [];
    if (orderTerms?.order?.length > 0) {
      toast.id = this.idService.generateNamespaceSequentialId(OrderNotificationsToastService.ID_PREFIX);
      toast.isComplex = orderTerms.strategy === undefined ? false : orderTerms.strategy !== Strategy.SPL;
      switch (orderTerms.strategy) {
        case Strategy.MLO:
          toasts = this.processMloOrder(toast, orderTerms as MultiLegOrderTerms);
          break;
        case Strategy.OTA:
        case Strategy.OCO:
        case Strategy.FTO:
          toasts = this.processComplexOrder(toast, orderTerms);
          break;
        default:
          toasts = this.processSimpleOrder(toast, orderTerms as EquityOrderTerms | OptionOrderTerms);
      }
    }
    return toasts;
  }

  /**
   * Process simple order
   */
  private processSimpleOrder(toast: OrderNotificationsToast, orderTerms: EquityOrderTerms | OptionOrderTerms):
  Array<OrderNotificationsToast> {
    const toasts: Array<OrderNotificationsToast> = [];
    const orders: Array<EquityOrderItem | OptionOrderItem> = orderTerms.order;
    orders.forEach((order: EquityOrderItem | OptionOrderItem): void => {
      if (order !== undefined) {
        this.setSingleOrderItem(toast, order, orderTerms);
        if (orderTerms.strategy !== undefined) {
          toast.strategy = orderTerms.strategy;
        }
        // check that the notification either has valid price and quantity or was rejected
        if ((toast.quantity !== undefined && toast.price !== undefined) || toast.isRejected) {
          toasts.push(toast);
        }
      }
    });
    return toasts;
  }

  /**
   * Process OTA/OCO/FTO order
   */
  private processComplexOrder(toast: OrderNotificationsToast, orderTerms: OrderTerms): Array<OrderNotificationsToast> {
    orderTerms.order = orderTerms.order.filter((o: EquityOrderItem | OptionOrderItem | OrderItem): boolean => o.legChanged === true);
    return this.processSimpleOrder(toast, orderTerms as EquityOrderTerms | OptionOrderTerms);
  }

  /**
   * Process MLO order
   */
  private processMloOrder(toast: OrderNotificationsToast, orderTerms: MultiLegOrderTerms): Array<OrderNotificationsToast> {
    const toasts: Array<OrderNotificationsToast> = [];
    if (orderTerms.order?.length === 2) {
      if (orderTerms.accountInfo !== undefined) {
        this.processAccountInfo(toast, orderTerms.accountInfo);
      }
      toast.strategy = orderTerms.strategy;
      this.setMloOrderStatus(toast, orderTerms);
      if (!toast.isRejected) {
        toast.hasLargeContent = true;
        this.setMloAvgPrice(toast, orderTerms);
      }
      const toastItems: Array<OrderNotificationsToastItem> = [];
      for (const order of orderTerms.order) {
        const item = this.getMloLegOrderItem(order, orderTerms);
        toastItems.push(item);
      }
      toast.items = toastItems;
      toasts.push(toast);
    }
    return toasts;
  }

  // remove the following code when the new MLO notification feature is ready
  /**
   * Process order notification data into simple or complex order
   */
  private processDataOld(notifications: WebsocketMessage<OrderNotificationsToast>): Array<OrderNotificationsToast> {
    const orderTerms = notifications.payload as OrderTerms;
    const toast: OrderNotificationsToast = this.getToast(notifications);
    let toasts: Array<OrderNotificationsToast> = [];
    if (orderTerms !== undefined && orderTerms.order !== undefined && orderTerms.order.length > 0) {
      toast.id = this.idService.generateNamespaceSequentialId(OrderNotificationsToastService.ID_PREFIX);
      toast.isComplex = orderTerms.strategy === undefined ? false : orderTerms.strategy !== Strategy.SPL;
      if (toast.isComplex) {
        this.processComplexOrderOld(toast, orderTerms);
        toasts.push(toast);
      } else {
        toasts = this.processSimpleOrderOld(toast, orderTerms);
      }
    }
    return toasts;
  }

  /**
   * Process simple order
   */
  private processSimpleOrderOld(toast: OrderNotificationsToast, orderTerms: OrderTerms):
  Array<OrderNotificationsToast> {
    const toasts: Array<OrderNotificationsToast> = [];
    const multiLegOrderTerms: EquityOrderTerms | OptionOrderTerms = orderTerms as EquityOrderTerms | OptionOrderTerms;
    const orders: Array<EquityOrderItem | OptionOrderItem> = multiLegOrderTerms.order;
    orders.forEach((order: EquityOrderItem | OptionOrderItem): void => {
      if (order !== undefined) {
        this.setOrderStatusOld(toast, order, orderTerms);
        this.processExtendedTerms(toast, order, multiLegOrderTerms);
        if (order.security !== undefined) {
          toast.symbol = order.security.type === SymbolType.OPTION ? order.security.displayTicker : order.security.symbol;
        }
        if (orderTerms.accountInfo !== undefined) {
          this.processAccountInfo(toast, orderTerms.accountInfo);
        }
        if (orderTerms.strategy !== undefined) {
          toast.strategy = orderTerms.strategy;
        }
        // check that the notification either has valid price and quantity or was rejected
        if ((toast.quantity !== undefined && toast.price !== undefined) || toast.isRejected) {
          toasts.push(toast);
        }
      }
    });
    return toasts;
  }

  private processComplexOrderOld(toast: OrderNotificationsToast, terms: OrderTerms | MultiLegOrderTerms): void {
    if (terms.accountInfo !== undefined) {
      toast.accountNumber = terms.accountInfo.accountNumber;
      if (terms.accountInfo.acctType !== undefined) {
        toast.accountTypeDescription = this.accountsDescriptionService.getTypeDescription(terms.accountInfo.acctType);
      }
    }
    this.processComplexStatusOld(toast, terms);
  }

  private processComplexStatusOld(toast: OrderNotificationsToast, orderTerms: OrderTerms | MultiLegOrderTerms): void {
    toast.strategy = orderTerms.strategy;
    if (orderTerms.strategy === Strategy.MLO) {
      const multiLegOrderTerms = orderTerms as MultiLegOrderTerms;
      this.setOrderStatusOld(toast, orderTerms.order[0], multiLegOrderTerms);
      this.processMloSymbolOld(toast, multiLegOrderTerms.order[0]);
    } else {
      toast.isRejected = false;
    }
  }

  private processMloSymbolOld(toast: OrderNotificationsToast, order: EquityOrderItem | OptionOrderItem): void {
    if (order !== undefined && order.security !== undefined) {
      toast.symbol = order.security.tickerElements === undefined ? order.security.symbol : order.security.tickerElements.underlyingCompany;
    }
  }

  private setOrderStatusOld(toast: OrderNotificationsToast, order: OrderItem, orderTerms: OrderTerms | MultiLegOrderTerms): void {
    let orderStatus;
    const multiLegOrder = orderTerms as MultiLegOrderTerms;
    const multiLegEntry = multiLegOrder.multilegEntry;

    if (multiLegEntry === undefined) {
      orderStatus = order.orderStatus;
    } else {
      orderStatus = multiLegEntry.orderStatus;
    }
    toast.isRejected = orderStatus === OrderNotificationsToastService.REJECTED;
  }
  // end of section

  private processExtendedTerms(toast: OrderNotificationsToast, order: EquityOrderItem | OptionOrderItem,
                               terms: EquityOrderTerms | OptionOrderTerms | MultiLegOrderTerms): void {
    if (order.extendedTerms !== undefined) {
      switch (order.extendedTerms.orderAction) {
        case EquityOrderAction.BUY:
        case OptionOrderAction.BUY_TO_OPEN:
        case OptionOrderAction.BUY_TO_COVER:
        case OptionOrderAction.BUY_TO_CLOSE:
          toast.action = toast.isRejected ? OrderNotificationsToastAction.BUY : OrderNotificationsToastAction.BOUGHT;
          break;
        case EquityOrderAction.SELL:
        case OptionOrderAction.SHORT_SELL:
        case OptionOrderAction.SELL_TO_CLOSE:
        case OptionOrderAction.SELL_TO_OPEN_COVERED:
        case OptionOrderAction.SELL_TO_OPEN_UNCOVERED:
          toast.action = toast.isRejected ? OrderNotificationsToastAction.SELL : OrderNotificationsToastAction.SOLD;
          break;
        default:
          toast.action = OrderNotificationsToastAction.UNKNOWN;
      }
    }
    if (toast.isRejected ) {
      this.setRejectedPriceAndQuantity(toast, order, (terms as MultiLegOrderTerms).multilegEntry, terms.pricePlan);
    } else {
      this.setPriceAndQuantity(toast, order);
    }
  }

  private setRejectedPriceAndQuantity(toast: OrderNotificationsToast,
                                      order: EquityOrderItem | OptionOrderItem,
                                      multiLegEntry: MultiLegEntry,
                                      pricePlan: PricePlan): void {
    if (multiLegEntry === undefined) {
      if (order !== undefined && order.extendedTerms !== undefined) {
        const equityExtendedTerms: EquityExtendedTerms = order.extendedTerms as EquityExtendedTerms;
        if (equityExtendedTerms.orderType === 'LIMIT' && equityExtendedTerms.triggerIndicator === false) {
          toast.price = equityExtendedTerms.limitPrice;
        } else {
          toast.price = equityExtendedTerms.triggerPrice;
        }
        if (toast.price === undefined) {
          toast.priceType = pricePlan;
        }
      }
    } else {
      if (pricePlan !== PricePlan.MARKET && pricePlan !== PricePlan.EVEN) {
        toast.price = multiLegEntry.extendedTerms.limitPrice;
      } else {
        toast.priceType = pricePlan;
      }
    }
    toast.quantity = order.quantity;
    toast.dollarAmt = order.dollarAmt;
  }

  private setPriceAndQuantity(toast: OrderNotificationsToast,
                              order: EquityOrderItem | OptionOrderItem): void {
    const terms = order.extendedTerms;
    if (terms?.orderFill !== undefined) {
      const fillData = Array.isArray(terms.orderFill) ? terms.orderFill[0] : terms.orderFill;
      if (fillData === undefined || Array.isArray(fillData) || typeof fillData !== 'object' || fillData === null) {
        return;
      }

      if (!('quantity' in fillData) || !('price' in fillData)) {
        return;
      }

      const normalizedFillData = fillData as { quantity: number; price: number };
      toast.quantity = normalizedFillData.quantity;
      toast.dollarAmt = order.dollarAmt;
      toast.price = normalizedFillData.price;
    }
  }

  private setMloAvgPrice(toast: OrderNotificationsToast,
                         orderItem: MultiLegOrderTerms): void {
    if (orderItem.multilegEntry !== undefined) {
      toast.price = orderItem.multilegEntry.weightedFillAveragePrice?.calculatedNetDebitCreditAmt;
      toast.priceType = orderItem.multilegEntry.weightedFillAveragePrice?.pricePlan;
    }
  }

  private getMloLegOrderItem(order: EquityOrderItem | OptionOrderItem,
                             multiLegOrderTerms: MultiLegOrderTerms): OrderNotificationsToastItem {
    const toast: OrderNotificationsToast = {};
    this.setMloOrderStatus(toast, multiLegOrderTerms);
    this.setSymbol(toast, order);
    this.processExtendedTerms(toast, order, multiLegOrderTerms);
    return {
      symbol: toast.symbol,
      quantity: toast.quantity,
      price: toast.price,
      action: toast.action,
      priceType: toast.priceType
    };
  }

  private setMloOrderStatus(toast: OrderNotificationsToast, orderTerms: MultiLegOrderTerms): void {
    const multiLegEntry = orderTerms.multilegEntry;
    toast.isRejected = multiLegEntry.orderStatus === OrderNotificationsToastService.REJECTED;
  }

  private setSingleOrderItem(toast: OrderNotificationsToast,
                             order: EquityOrderItem | OptionOrderItem,
                             orderTerms: EquityOrderTerms | OptionOrderTerms): void {
    this.setOrderStatus(toast, order);
    this.processExtendedTerms(toast, order, orderTerms);
    this.setSymbol(toast, order);
    this.processAccountInfo(toast, orderTerms.accountInfo);
  }

  private processAccountInfo(toast: OrderNotificationsToast, accountInfo: OrderAccountInfo): void {
    if (accountInfo.acctType !== undefined) {
      toast.accountTypeDescription = this.accountsDescriptionService.getTypeDescription(accountInfo.acctType);
    }
    toast.accountNumber = accountInfo.accountNumber;
    toast.accountCurrency = accountInfo.currency;
  }

  private setSymbol(toast: OrderNotificationsToast, order: EquityOrderItem | OptionOrderItem): void {
    if (order?.security !== undefined) {
      toast.symbol = order.security.type === SymbolType.OPTION ? order.security.displayTicker : order.security.symbol;
    }
  }

  private setOrderStatus(toast: OrderNotificationsToast, order: OrderItem): void {
    toast.isRejected = order.orderStatus === OrderNotificationsToastService.REJECTED;
  }

}
